import React, { useState, useCallback, useRef, useEffect } from 'react';
import { CALIB_STEPS } from '../../config/constants';
import { CalibSensorReadouts } from '../../hooks/useCalibration';
import type { RppgState } from '../../hooks/useRppg';
import type { CalibTopStep } from '../../types';
import { computeHomography, cornersFromEdgePoints, PAPER_DST, type Point2D } from '../../engine/Homography';

interface CalibrationOverlayProps {
  calibStep: number;
  currentCalibPoint: { row: number; col: number } | null;
  acceptedPoints: number;
  totalPoints: number;
  sensorReadouts: CalibSensorReadouts;
  headStable: boolean;
  onCalibPointClick: () => void;
  onAdvanceStep: () => void;
  eyeTrackerCalibClick: (normX: number, normY: number) => void;
  stylusHandlers: {
    onPointerDown: (e: React.PointerEvent) => void;
    onPointerMove: (e: React.PointerEvent) => void;
    onPointerUp: (e: React.PointerEvent) => void;
  };
  voiceRms: number;
  keyCount: number;
  stylusSamples: number;
  rppg: RppgState;
  /** Called when paper 4-point calibration completes with the homography matrix */
  onPaperCalibrated?: (H: Float64Array, corners: Point2D[]) => void;
  /** Nose Y position normalized 0-1 (0=top of frame). Used for camera angle check. */
  faceNormY: number | null;
}

const TOP_STEP_SUBTITLES: Record<CalibTopStep, string> = {
  1: '시험 시작 전 응시자의 정보를 확인하는 단계에요',
  2: '시험 시작 전 환경을 확인하고 있어요',
  3: '시험 시작 전 긴장과 불안을 낮춰 보세요',
};

const TOP_STEP_LABELS: Record<CalibTopStep, string> = {
  1: '신분 확인',
  2: '환경 확인',
  3: '마음 준비',
};

/**
 * Step pills row: shows three pills (Step 1 / Step 2 / Step 3) connected by
 * dotted separators. Current step is wide + labeled, completed steps show a
 * green check, future steps show just "Step N".
 */
function StepPills({ current }: { current: CalibTopStep }) {
  const pills: CalibTopStep[] = [1, 2, 3];
  return (
    <div className="calib-steps">
      {pills.map((n, idx) => {
        const isActive = n === current;
        const isCompleted = n < current;
        const cls = isActive ? 'calib-step-pill active'
                  : isCompleted ? 'calib-step-pill completed'
                  : 'calib-step-pill';
        return (
          <React.Fragment key={n}>
            <div className={cls}>
              <span className="calib-step-pill-num">Step {n}</span>
              {isActive && (
                <span className="calib-step-pill-label">{TOP_STEP_LABELS[n]}</span>
              )}
              {isCompleted && <span className="calib-step-check" aria-label="완료" />}
            </div>
            {idx < pills.length - 1 && (
              <div className="calib-step-dots"><span /></div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export function CalibrationOverlay({
  calibStep, currentCalibPoint,
  acceptedPoints, totalPoints, sensorReadouts, headStable,
  onCalibPointClick, onAdvanceStep, eyeTrackerCalibClick,
  stylusHandlers, voiceRms, keyCount, stylusSamples, rppg,
  onPaperCalibrated,
  faceNormY,
}: CalibrationOverlayProps) {
  const step = CALIB_STEPS[calibStep] || CALIB_STEPS[0];
  const topStep = step.topStep;
  const n = 3;

  // ── Camera angle check state ──
  const FACE_Y_MIN = 0.25;
  const FACE_Y_MAX = 0.50;
  const CAMERA_OK_HOLD_MS = 2000;
  const cameraInRangeRef = useRef(false);
  const cameraOkSinceRef = useRef(0);
  const [cameraOk, setCameraOk] = useState(false);

  useEffect(() => {
    if (step.sensor !== 'camera' || faceNormY == null) return;
    const inRange = faceNormY >= FACE_Y_MIN && faceNormY <= FACE_Y_MAX;
    if (inRange && !cameraInRangeRef.current) {
      cameraOkSinceRef.current = Date.now();
    }
    cameraInRangeRef.current = inRange;
    if (inRange && Date.now() - cameraOkSinceRef.current >= CAMERA_OK_HOLD_MS) {
      setCameraOk(true);
    } else if (!inRange) {
      setCameraOk(false);
    }
  }, [step.sensor, faceNormY]);

  // ── Paper edge-based 8-click calibration state ──
  // 8 points: 2 per edge, ordered top, right, bottom, left.
  // Corners are derived via line intersections → off-frame edges work.
  const [edgePoints, setEdgePoints] = useState<Point2D[]>([]);
  const [paperCorners, setPaperCorners] = useState<Point2D[]>([]);
  const [paperDone, setPaperDone] = useState(false);

  const handlePaperReset = useCallback(() => {
    setEdgePoints([]);
    setPaperCorners([]);
    setPaperDone(false);
  }, []);

  const handlePaperClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (step.sensor !== 'paper' || paperDone) return;
    const container = e.currentTarget;
    const rect = container.getBoundingClientRect();
    const video = container.querySelector('video');
    if (!video) return;

    // Account for object-fit: contain letterboxing + CSS mirror (scaleX(-1))
    const containerW = rect.width;
    const containerH = rect.height;
    const videoW = video.videoWidth || containerW;
    const videoH = video.videoHeight || containerH;
    const containerAR = containerW / containerH;
    const videoAR = videoW / videoH;

    let renderW: number, renderH: number, offsetX: number, offsetY: number;
    if (videoAR > containerAR) {
      renderW = containerW;
      renderH = containerW / videoAR;
      offsetX = 0;
      offsetY = (containerH - renderH) / 2;
    } else {
      renderH = containerH;
      renderW = containerH * videoAR;
      offsetX = (containerW - renderW) / 2;
      offsetY = 0;
    }

    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;

    // Normalize to video coords, mirror-correct (video has scaleX(-1)).
    // No bounds check — letterbox / off-frame clicks are intentional for edge fitting.
    const pt = {
      x: 1 - (clickX - offsetX) / renderW,
      y: (clickY - offsetY) / renderH,
    };
    const next = [...edgePoints, pt];
    setEdgePoints(next);

    if (next.length >= 8) {
      const corners = cornersFromEdgePoints(next);
      if (corners) {
        setPaperCorners(corners);
        setPaperDone(true);
        const H = computeHomography(corners, PAPER_DST);
        if (H && onPaperCalibrated) onPaperCalibrated(H, corners);
      } else {
        // Degenerate edges — reset and let user retry
        setEdgePoints([]);
      }
    }
  }, [step.sensor, edgePoints, paperDone, onPaperCalibrated]);

  const handlePointClick = () => {
    if (!currentCalibPoint) return;
    const normX = ((currentCalibPoint.col + 0.5) / n) - 0.5;
    const normY = ((currentCalibPoint.row + 0.5) / n) - 0.5;
    eyeTrackerCalibClick(normX, normY);
    onCalibPointClick();
  };

  // Per-sub-step progress (0..1) for the in-body progress bar
  const subProgress = (() => {
    switch (step.sensor) {
      case 'key':        return Math.min(1, keyCount / 10);
      case 'stylus':     return Math.min(1, stylusSamples / 5);
      case 'eyetracker': return totalPoints > 0 ? acceptedPoints / totalPoints : 0;
      case 'head':       return headStable ? 1 : Math.min(0.95, sensorReadouts.headSamples / 120);
      case 'voice':      return Math.min(1, sensorReadouts.voiceSamples / 50);
      case 'camera':     return cameraOk ? 1 : (faceNormY != null ? 0.5 : 0);
      case 'paper':      return paperDone ? 1 : edgePoints.length / 8;
      case 'rppg':       return rppg.displayBpm != null ? 1 : rppg.warmupProgress;
      default:           return 0;
    }
  })();

  const canAdvance = (() => {
    switch (step.sensor) {
      case 'eyetracker': return acceptedPoints >= totalPoints;
      case 'head':       return headStable;
      case 'key':        return keyCount >= 10;
      case 'stylus':     return stylusSamples >= 5;
      case 'voice':      return sensorReadouts.voiceSamples >= 50;
      case 'camera':     return cameraOk;
      case 'paper':      return paperDone;
      case 'rppg':       return true;
      default:           return false;
    }
  })();

  const isLastStep = calibStep >= CALIB_STEPS.length - 1;
  const ctaLabel = step.sensor === 'rppg' || isLastStep
    ? '시험 시작하기'
    : '다음으로 →';

  // ─── Paper step is handled inside the modal shell (below) ───
  // It renders a large webcam feed inside the modal body for clicking.

  // ─── Eye tracker fullscreen step ───
  if (step.sensor === 'eyetracker') {
    return (
      <div className="calib-eye-layer">
        <div className="calib-eye-topbar">
          <span className="calib-title">시험 체크인 · Step 2 환경 확인</span>
          <span style={{ color: 'var(--calib-gray-400)', fontSize: 14 }}>시선 보정</span>
        </div>
        {currentCalibPoint && (
          <div
            className="calib-point"
            style={{
              left: `${((currentCalibPoint.col + 0.5) / n) * 100}%`,
              top: `${((currentCalibPoint.row + 0.5) / n) * 100}%`,
              background: 'var(--calib-primary-500)',
              border: '3px solid #fff',
            }}
            onClick={handlePointClick}
          />
        )}
        <div className="calib-eye-progress">
          <div className="calib-eye-progress-label">
            {canAdvance
              ? '시선 보정 완료!'
              : `${acceptedPoints} / ${totalPoints} — 점을 클릭하세요`}
          </div>
          <div className="calib-progress">
            <div
              className="calib-progress-fill"
              style={{ width: `${subProgress * 100}%` }}
            />
          </div>
          {canAdvance && (
            <button
              className="calib-cta enabled"
              style={{ marginTop: 12, width: 300 }}
              onClick={onAdvanceStep}
            >
              다음으로 →
            </button>
          )}
        </div>
      </div>
    );
  }

  // ─── Modal shell (all non-eyetracker steps) ───
  return (
    <div className="calib-modal-scrim">
      <div className="calib-modal">
        <div className="calib-modal-inner">
          {/* Header */}
          <div className="calib-header">
            <div className="calib-header-row">
              <div className="calib-title">시험 체크인</div>
              <StepPills current={topStep} />
            </div>
            <div className="calib-subtitle">
              <span>{TOP_STEP_SUBTITLES[topStep]}</span>
              {step.subLabel && <span className="calib-sub-label">{step.subLabel}</span>}
            </div>
          </div>

          {/* Body */}
          <div className="calib-body">
            <div className="calib-prompt">{step.prompt}</div>

            {step.showInput && (
              <input
                type="text"
                className="calib-input"
                placeholder="예: 서울양재고등학교"
                autoFocus
              />
            )}

            {step.showDraw && (
              <canvas
                className="calib-canvas"
                width={450}
                height={140}
                onPointerDown={stylusHandlers.onPointerDown}
                onPointerMove={stylusHandlers.onPointerMove}
                onPointerUp={stylusHandlers.onPointerUp}
                onPointerLeave={stylusHandlers.onPointerUp}
              />
            )}

            {step.sensor === 'head' && (
              <div className={`calib-status-pill ${headStable ? 'ok' : ''}`}>
                {headStable
                  ? '✓ 안정됨'
                  : `정면 응시 중… (${sensorReadouts.headSamples} samples)`}
              </div>
            )}

            {step.sensor === 'voice' && (
              <div className="calib-status-pill">
                🎤 RMS {voiceRms.toFixed(4)} · {sensorReadouts.voiceSamples}/50
              </div>
            )}

            {step.sensor === 'camera' && (
              <CameraAngleGuide faceNormY={faceNormY} ok={cameraOk} />
            )}

            {step.sensor === 'rppg' && <HeartRateRing rppg={rppg} />}

            {step.sensor === 'paper' && (
              <PaperCalibWebcam
                edgePoints={edgePoints}
                corners={paperCorners}
                done={paperDone}
                onClick={handlePaperClick}
                onReset={handlePaperReset}
              />
            )}

            <div className="calib-progress">
              <div
                className="calib-progress-fill"
                style={{ width: `${subProgress * 100}%` }}
              />
            </div>
          </div>

          {/* Footer CTA */}
          <div className="calib-footer">
            <button
              className={`calib-cta ${canAdvance ? 'enabled' : 'disabled'}`}
              onClick={onAdvanceStep}
              disabled={!canAdvance}
            >
              {ctaLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Camera angle guide — shows where the face is vertically in the frame
 * and guides the user to adjust the webcam so both face and desk are visible.
 */
function CameraAngleGuide({ faceNormY, ok }: { faceNormY: number | null; ok: boolean }) {
  const FACE_Y_MIN = 0.25;
  const FACE_Y_MAX = 0.50;

  const guidance = (() => {
    if (faceNormY == null) return { text: '얼굴을 인식하는 중…', color: '#999' };
    if (faceNormY < FACE_Y_MIN) return { text: '카메라를 위로 올려주세요 (얼굴이 너무 위에 있어요)', color: '#FF8A3D' };
    if (faceNormY > FACE_Y_MAX) return { text: '카메라를 아래로 내려주세요 (얼굴이 너무 아래에 있어요)', color: '#FF8A3D' };
    if (ok) return { text: '카메라 각도가 적절합니다!', color: 'var(--calib-primary-500)' };
    return { text: '좋아요, 잠시 유지해주세요…', color: '#3DA5FF' };
  })();

  const markerY = faceNormY != null ? Math.max(0, Math.min(1, faceNormY)) : 0.5;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, padding: '12px 0' }}>
      {/* Vertical bar showing face position */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <div style={{ position: 'relative', width: 32, height: 180, borderRadius: 16, background: '#1a1a2e', border: '1px solid #333' }}>
          {/* Ideal zone */}
          <div style={{
            position: 'absolute', left: 2, right: 2,
            top: `${FACE_Y_MIN * 100}%`, height: `${(FACE_Y_MAX - FACE_Y_MIN) * 100}%`,
            borderRadius: 8, background: 'rgba(94,210,143,0.25)', border: '1px solid rgba(94,210,143,0.5)',
          }} />
          {/* Face position marker */}
          {faceNormY != null && (
            <div style={{
              position: 'absolute', left: '50%', top: `${markerY * 100}%`,
              transform: 'translate(-50%, -50%)', width: 20, height: 20, borderRadius: '50%',
              background: guidance.color, border: '2px solid #fff',
              transition: 'top 0.15s ease-out',
            }} />
          )}
          {/* Labels */}
          <span style={{ position: 'absolute', top: -20, left: '50%', transform: 'translateX(-50%)', fontSize: 11, color: '#888' }}>위</span>
          <span style={{ position: 'absolute', bottom: -20, left: '50%', transform: 'translateX(-50%)', fontSize: 11, color: '#888' }}>아래</span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxWidth: 260 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: guidance.color }}>
            {guidance.text}
          </div>
          <div style={{ fontSize: 12, color: '#999', lineHeight: 1.5 }}>
            얼굴이 화면 상단 1/3에 오도록 카메라 각도를 맞추면 책상 위 시험지까지 함께 비출 수 있습니다.
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Step 3 마음 준비 — live heart-rate ring fed by the rPPG hook.
 *
 * Shows:
 *   - the smoothed display bpm (or "—" while warming up / when signal is poor)
 *   - a sub-label that conveys the current signal state (warming up %, poor
 *     lighting, fair, good)
 */
function HeartRateRing({ rppg }: { rppg: RppgState }) {
  const { displayBpm, quality, warmupProgress } = rppg;

  const valueText = displayBpm != null ? String(displayBpm) : '—';
  const unitText = (() => {
    if (quality === 'warming_up') {
      return `측정 준비 중… ${Math.round(warmupProgress * 100)}%`;
    }
    if (displayBpm == null) return '신호 약함 · 조명 확인';
    if (quality === 'poor')  return 'bpm · 신호 약함';
    if (quality === 'fair')  return 'bpm · 신호 보통';
    return 'bpm · 측정 중';
  })();

  return (
    <div className="calib-hr-ring">
      <div className="calib-hr-ring-inner">
        <span className="calib-hr-ring-label">현재 심박수</span>
        <span className="calib-hr-ring-value">{valueText}</span>
        <span className="calib-hr-ring-unit">{unitText}</span>
      </div>
    </div>
  );
}

/**
 * Paper calibration: large webcam feed inside the modal body.
 * User clicks 2 points on each of the 4 edges (top → right → bottom → left,
 * 8 clicks total). Corners are derived via line intersections, so edges that
 * run off-frame are supported — the user clicks wherever the edge is visible.
 */
function PaperCalibWebcam({
  edgePoints, corners, done, onClick, onReset,
}: {
  edgePoints: Point2D[];
  corners: Point2D[];
  done: boolean;
  onClick: (e: React.MouseEvent<HTMLDivElement>) => void;
  onReset: () => void;
}) {
  // 8-click labels, 2 per edge
  const EDGE_LABELS = [
    '① 상단 변 · 첫번째', '② 상단 변 · 두번째',
    '③ 우측 변 · 첫번째', '④ 우측 변 · 두번째',
    '⑤ 하단 변 · 첫번째', '⑥ 하단 변 · 두번째',
    '⑦ 좌측 변 · 첫번째', '⑧ 좌측 변 · 두번째',
  ];
  const EDGE_COLORS = [
    '#FF8A3D', '#FF8A3D', // top
    '#3DA5FF', '#3DA5FF', // right
    '#B45DFF', '#B45DFF', // bottom
    '#FFCE3D', '#FFCE3D', // left
  ];
  const videoRef = React.useRef<HTMLVideoElement>(null);

  // Clone the webcam stream to this video element
  React.useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    const main = document.getElementById('webcamVideo') as HTMLVideoElement | null;
    if (main?.srcObject && !el.srcObject) {
      el.srcObject = main.srcObject;
    }
  });

  // Build edge-line preview: once both points of an edge are placed, draw
  // the fitted line extended far past both samples so the user sees the
  // implicit edge (including the off-frame portion).
  const edgeLines: { p1: Point2D; p2: Point2D; color: string }[] = [];
  const EDGE_COLOR_BY_IDX = ['#FF8A3D', '#3DA5FF', '#B45DFF', '#FFCE3D'];
  for (let e = 0; e < 4; e++) {
    const a = edgePoints[e * 2];
    const b = edgePoints[e * 2 + 1];
    if (!a || !b) continue;
    // Extend line by ±3× in the direction of (b - a)
    const dx = b.x - a.x, dy = b.y - a.y;
    edgeLines.push({
      p1: { x: a.x - dx * 3, y: a.y - dy * 3 },
      p2: { x: b.x + dx * 3, y: b.y + dy * 3 },
      color: EDGE_COLOR_BY_IDX[e],
    });
  }

  return (
    <div className="calib-paper-webcam" onClick={done ? undefined : onClick}>
      <video ref={videoRef} autoPlay playsInline muted />
      <svg style={{ transform: 'scaleX(-1)', overflow: 'visible' }}>
        {/* Fitted edge lines */}
        {edgeLines.map((ln, i) => (
          <line key={`ln${i}`}
            x1={`${ln.p1.x * 100}%`} y1={`${ln.p1.y * 100}%`}
            x2={`${ln.p2.x * 100}%`} y2={`${ln.p2.y * 100}%`}
            stroke={ln.color} strokeWidth="2" strokeDasharray="6 4" opacity="0.85" />
        ))}
        {/* Clicked edge points */}
        {edgePoints.map((pt, i) => {
          const cx = `${pt.x * 100}%`;
          const cy = `${pt.y * 100}%`;
          return (
            <circle key={`pt${i}`} cx={cx} cy={cy} r="8"
              fill={EDGE_COLORS[i]} stroke="#fff" strokeWidth="2" />
          );
        })}
        {/* Final quad once all 4 corners are derived */}
        {corners.length === 4 && (
          <polygon
            points={corners.map(p => `${p.x * 100}%,${p.y * 100}%`).join(' ')}
            fill="rgba(94,210,143,0.15)" stroke="var(--calib-primary-500)" strokeWidth="3"
          />
        )}
        {corners.length === 4 && corners.map((pt, i) => (
          <circle key={`c${i}`} cx={`${pt.x * 100}%`} cy={`${pt.y * 100}%`} r="6"
            fill="var(--calib-primary-500)" stroke="#fff" strokeWidth="2" />
        ))}
      </svg>
      {!done && (
        <div style={{
          position: 'absolute', bottom: 8, left: '50%', transform: 'translateX(-50%)',
          background: 'rgba(0,0,0,0.75)', color: '#fff', padding: '6px 16px',
          borderRadius: 8, fontSize: 13, fontWeight: 600, zIndex: 3,
          whiteSpace: 'nowrap',
        }}>
          {EDGE_LABELS[edgePoints.length] ?? '완료'} 클릭 (변이 프레임 밖이어도 OK)
        </div>
      )}

      {(done || edgePoints.length > 0) && (
        <button
          type="button"
          onClick={(ev) => { ev.stopPropagation(); onReset(); }}
          style={{
            position: 'absolute', top: 8, right: 8, zIndex: 4,
            background: 'rgba(0,0,0,0.7)', color: '#fff',
            border: '1px solid rgba(255,255,255,0.4)', borderRadius: 8,
            padding: '6px 12px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
          }}
        >
          다시
        </button>
      )}
    </div>
  );
}
